import { useCallback, useEffect, useState, type ReactNode } from 'react';

import type { RequisitionNotificationRecipient, RequisitionNotificationSettings } from '@customTypes/requisition';
import {
  getRequisitionNotificationSettings,
  saveRequisitionNotificationSettings,
} from '@store/actions/requisitionActions';

import { RequisitionNotificationsCard } from './RequisitionNotificationsCard';

export interface RequisitionNotificationsProps {
  requisitionId: string;
  /** When true, skip the initial fetch until `enabled` becomes true (patient chart order expand). */
  lazy?: boolean;
  enabled?: boolean;
  /**
   * When true, render nothing while loading and when the widget is unavailable
   * (403 / 404 / empty recipients / load failure). Used on patient chart so accordion chrome does not flash.
   */
  hideUntilReady?: boolean;
  hideHeader?: boolean;
  onAvailabilityChange?: (availability: RequisitionNotificationsAvailability) => void;
  /** Wrap the card (e.g. patient-chart accordion). Only called once the widget is available. */
  renderShell?: (args: { children: ReactNode }) => ReactNode;
}

export type RequisitionNotificationsAvailability = 'loading' | 'available' | 'hidden';

const cloneRecipients = (recipients: RequisitionNotificationRecipient[]) =>
  recipients.map(recipient => ({ ...recipient }));

const SAVE_ERROR = 'Could not save notification settings. Try again.';

export function RequisitionNotifications(props: RequisitionNotificationsProps) {
  const {
    requisitionId,
    lazy = false,
    enabled = true,
    hideUntilReady = false,
    hideHeader = false,
    onAvailabilityChange,
    renderShell,
  } = props;

  const [hasFetched, setHasFetched] = useState(false);
  const [loading, setLoading] = useState(!lazy);
  const [hidden, setHidden] = useState(false);
  const [recipients, setRecipients] = useState<RequisitionNotificationRecipient[]>([]);
  const [savedRecipients, setSavedRecipients] = useState<RequisitionNotificationRecipient[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const applySettings = useCallback((settings: RequisitionNotificationSettings) => {
    const nextRecipients = cloneRecipients(settings.recipients);
    setRecipients(nextRecipients);
    setSavedRecipients(cloneRecipients(nextRecipients));
  }, []);

  useEffect(() => {
    if (lazy && !enabled) return;
    if (hasFetched) return;

    let cancelled = false;
    getRequisitionNotificationSettings(requisitionId)
      .then(settings => {
        if (cancelled) return;
        if (settings.recipients.length === 0) {
          setHidden(true);
          setErrorMessage(null);
          return;
        }
        applySettings(settings);
        setHidden(false);
        setErrorMessage(null);
      })
      .catch(() => {
        if (cancelled) return;
        setHidden(true);
        setErrorMessage(null);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setHasFetched(true);
      });

    return () => {
      cancelled = true;
    };
  }, [applySettings, enabled, hasFetched, lazy, requisitionId]);

  const showLoading = loading || (lazy && enabled && !hasFetched);
  const availability: RequisitionNotificationsAvailability = showLoading ? 'loading' : hidden ? 'hidden' : 'available';

  useEffect(() => {
    onAvailabilityChange?.(availability);
  }, [availability, onAvailabilityChange]);

  if (lazy && !enabled && !hasFetched) {
    return null;
  }

  if (hideUntilReady && (showLoading || hidden)) {
    return null;
  }

  if (hidden) {
    return null;
  }

  const dirty = JSON.stringify(recipients) !== JSON.stringify(savedRecipients);

  const handleToggleRecipient = (providerId: string) => {
    setSavedFlash(false);
    setRecipients(rows =>
      rows.map(row => (row.provider_id === providerId ? { ...row, selected: !row.selected } : row)),
    );
  };

  const handleSelectAll = () => {
    setSavedFlash(false);
    setRecipients(rows => rows.map(row => ({ ...row, selected: true })));
  };

  const handleDeselectAll = () => {
    setSavedFlash(false);
    setRecipients(rows => rows.map(row => ({ ...row, selected: false })));
  };

  const handleCancel = () => {
    setSavedFlash(false);
    setRecipients(cloneRecipients(savedRecipients));
  };

  const handleSave = async () => {
    if (saving) return;
    setErrorMessage(null);
    setSaving(true);

    const allChecked = recipients.length > 0 && recipients.every(row => row.selected);
    const payload = allChecked
      ? { mode: 'DEFAULT' as const, selected_provider_ids: [] as string[] }
      : {
          mode: 'CUSTOM' as const,
          selected_provider_ids: recipients.filter(row => row.selected).map(row => row.provider_id),
        };

    try {
      const settings = await saveRequisitionNotificationSettings(requisitionId, payload);
      applySettings(settings);
      setSavedFlash(true);
    } catch {
      setErrorMessage(SAVE_ERROR);
    } finally {
      setSaving(false);
    }
  };

  const card = (
    <RequisitionNotificationsCard
      requisitionId={requisitionId}
      recipients={recipients}
      dirty={dirty}
      saving={saving}
      savedFlash={savedFlash}
      loading={showLoading}
      errorMessage={errorMessage}
      hideHeader={hideHeader}
      onToggleRecipient={handleToggleRecipient}
      onSelectAll={handleSelectAll}
      onDeselectAll={handleDeselectAll}
      onCancel={handleCancel}
      onSave={() => {
        void handleSave();
      }}
    />
  );

  if (renderShell) {
    return <>{renderShell({ children: card })}</>;
  }

  return card;
}
